import React, { useEffect, useRef } from 'react';
import { Star } from 'lucide-react';
import Container from '../ui/Container';

interface Testimonial {
  name: string;
  position: string;
  quote: string;
  rating: number;
  image: string;
}

const TestimonialCard = ({ testimonial }: { testimonial: Testimonial }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, []);

  return (
    <div 
      ref={cardRef} 
      className="bg-white rounded-lg shadow-lg p-6 opacity-0 transition-all duration-300 hover:shadow-xl"
    >
      <div className="flex items-center mb-4">
        <img 
          src={testimonial.image} 
          alt={testimonial.name} 
          className="w-16 h-16 rounded-full object-cover mr-4"
        />
        <div>
          <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
          <p className="text-gray-600 text-sm">{testimonial.position}</p>
        </div>
      </div>
      
      <div className="flex mb-4">
        {[...Array(5)].map((_, i) => (
          <Star 
            key={i} 
            size={18} 
            className={`${i < testimonial.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'} mr-1`} 
          />
        ))}
      </div>
      
      <p className="text-gray-700 italic">"{testimonial.quote}"</p>
    </div>
  );
};

const PlatformTestimonials = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const testimonials: Testimonial[] = [
    {
      name: 'Vikram Mehta',
      position: 'Business Executive',
      quote: 'DriversOnCall has been a game-changer for my business travel. I can always count on finding a professional driver, even on short notice.',
      rating: 5,
      image: 'https://images.pexels.com/photos/2379005/pexels-photo-2379005.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
    },
    {
      name: 'Ananya Sharma',
      position: 'Event Planner',
      quote: 'As an event planner, I need reliable transportation for my clients. DriversOnCall consistently delivers excellent service.',
      rating: 5,
      image: 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
    },
    {
      name: 'Rajiv Patel',
      position: 'Hotel Manager',
      quote: 'We recommend DriversOnCall to all our hotel guests. The service is prompt, professional, and our guests love the convenience.',
      rating: 4,
      image: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
      <Container>
        <div ref={sectionRef} className="opacity-0 transition-opacity duration-1000">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Customer Testimonials</h2>
            <div className="w-20 h-1 bg-orange-500 mx-auto mb-6"></div>
            <p className="text-blue-100 max-w-2xl mx-auto">
              Hear what our satisfied customers have to say about their experience with DriversOnCall.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={index} testimonial={testimonial} />
            ))}
          </div>
          
          <div className="mt-16 text-center">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 inline-block">
              <h3 className="text-2xl font-bold mb-4">Join Our Satisfied Customers</h3>
              <p className="mb-6 text-blue-100">
                Experience the convenience and reliability of DriversOnCall today. Sign up now to get started.
              </p>
              <a 
                href="https://driversoncall.co" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block bg-orange-600 text-white px-6 py-3 rounded-md font-medium hover:bg-orange-500 transition-colors"
              >
                Sign Up for DriversOnCall
              </a>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default PlatformTestimonials;